/**
 * DiscardChangesDialog — confirmation shown when a dirty form is about to be
 * closed (ADR-114, enmienda 1: descarte de cambios en formularios).
 *
 * Use it for explicit Cancel/Close buttons of dirty forms; the Dialog
 * primitive uses the same confirmation internally for ESC when `dirty`.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './AlertDialog';

export interface DiscardChangesDialogProps {
  open: boolean;
  /** Called when the user confirms discarding the changes. */
  onDiscard: () => void;
  /** Called when the user keeps editing (cancels the discard). */
  onCancel: () => void;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  'data-testid'?: string;
}

export function DiscardChangesDialog({
  open,
  onDiscard,
  onCancel,
  title = 'Discard changes?',
  message = 'You have unsaved changes. If you close now, they will be lost.',
  confirmLabel = 'Discard',
  cancelLabel = 'Keep editing',
  'data-testid': testId = 'discard-changes-dialog',
}: DiscardChangesDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <AlertDialogContent ariaLabel={title} data-testid={testId}>
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6">
          <AlertDialogCancel onClick={onCancel}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction onClick={onDiscard}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
